import { useEffect, useState, useCallback } from "react";
import { Filter, ScrollText } from "lucide-react";
import { toast } from "sonner";
import { API, formatApiError } from "../lib/api";
import Pagination from "../components/Pagination";
import StatusBadge from "../components/StatusBadge";
import { fmtDate } from "./Dashboard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function AuditLogs() {
  const [filters, setFilters] = useState({ username: "", action: "", resource_type: "", result: "", ip: "", date_from: "", date_to: "" });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback((p = page, ps = pageSize, f = filters) => {
    setLoading(true);
    const params = Object.fromEntries(Object.entries({ ...f, page: p, page_size: ps }).filter(([, v]) => v));
    API.get("/admin/audit", { params })
      .then((r) => { setResult(r.data); setPage(p); })
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [page, pageSize, filters]);

  useEffect(() => { load(1); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const set = (k) => (e) => setFilters({ ...filters, [k]: e?.target ? e.target.value : e });

  return (
    <div className="space-y-6" data-testid="audit-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-heading font-bold flex items-center gap-2">
          <ScrollText className="h-7 w-7 text-[#3A75C4]" /> Journal d'audit
        </h1>
        <p className="text-sm text-muted-foreground">Tracabilite complete des actions sur la plateforme. Acces reserve aux utilisateurs autorises.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3" data-testid="audit-filters">
        <Input placeholder="Utilisateur" value={filters.username} onChange={set("username")} data-testid="audit-f-user" />
        <Select value={filters.action || "all"} onValueChange={(v) => setFilters({ ...filters, action: v === "all" ? "" : v })}>
          <SelectTrigger data-testid="audit-f-action"><SelectValue placeholder="Action" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toutes actions</SelectItem>
            {(result?.actions || []).map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filters.resource_type || "all"} onValueChange={(v) => setFilters({ ...filters, resource_type: v === "all" ? "" : v })}>
          <SelectTrigger data-testid="audit-f-module"><SelectValue placeholder="Module" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous modules</SelectItem>
            {["auth", "search", "person", "vehicle", "license", "organization", "import", "user", "role", "settings"].map((m) => (
              <SelectItem key={m} value={m}>{m}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filters.result || "all"} onValueChange={(v) => setFilters({ ...filters, result: v === "all" ? "" : v })}>
          <SelectTrigger data-testid="audit-f-result"><SelectValue placeholder="Resultat" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous resultats</SelectItem>
            {["SUCCES", "ECHEC", "BLOQUE"].map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
          </SelectContent>
        </Select>
        <Input placeholder="Adresse IP" value={filters.ip} onChange={set("ip")} data-testid="audit-f-ip" />
        <Input type="date" value={filters.date_from} onChange={set("date_from")} data-testid="audit-f-from" />
        <div className="flex gap-2">
          <Input type="date" value={filters.date_to} onChange={set("date_to")} data-testid="audit-f-to" />
          <Button onClick={() => load(1)} className="bg-[#009E60] hover:bg-[#00804e] text-white shrink-0" data-testid="audit-apply">
            <Filter className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {loading && !result ? (
        <div className="space-y-2">{[...Array(8)].map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}</div>
      ) : (
        <>
          <div className="overflow-x-auto rounded-lg border bg-card">
            <table className="w-full text-sm">
              <thead className="bg-accent/60 text-left">
                <tr>{["Date", "Utilisateur", "Action", "Module", "Resultat", "IP", "Details"].map((h) => (
                  <th key={h} className="px-3 py-2.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{h}</th>
                ))}</tr>
              </thead>
              <tbody>
                {(result?.items || []).map((a, i) => (
                  <tr key={a.id} className="border-t hover:bg-accent/40 transition-colors" data-testid={`audit-row-${i}`}>
                    <td className="px-3 py-2 text-xs whitespace-nowrap mono">{fmtDate(a.ts)}</td>
                    <td className="px-3 py-2 text-xs font-medium">{a.username || "systeme"}</td>
                    <td className="px-3 py-2 text-xs">{a.action}</td>
                    <td className="px-3 py-2 text-xs">{a.resource_type || "-"}</td>
                    <td className="px-3 py-2"><StatusBadge value={a.result} /></td>
                    <td className="px-3 py-2 text-xs mono">{a.ip || "-"}</td>
                    <td className="px-3 py-2 text-xs text-muted-foreground max-w-56 truncate">
                      {a.metadata?.query ? `"${a.metadata.query}"` : a.metadata?.fichier || a.metadata?.username || a.metadata?.operation || "-"}
                    </td>
                  </tr>
                ))}
                {result && result.items.length === 0 && (
                  <tr><td colSpan={7} className="px-3 py-10 text-center text-muted-foreground">Aucun evenement.</td></tr>
                )}
              </tbody>
            </table>
          </div>
          {result && (
            <Pagination total={result.total} page={page} pageSize={pageSize}
              onPage={(p) => load(p)} onPageSize={(s) => { setPageSize(s); load(1, s); }} testid="audit-pagination" />
          )}
        </>
      )}
    </div>
  );
}
